import {Button, view, CodePuncher, Icon, Spinner} from '@sail/ui';
import {LightCard, MetaRow} from '@stripe-internal/conversations-ui';
import {checkCircle} from '@sail/icons/react/Icon';
import {
  type InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl, defineMessages} from 'react-intl';

const messages = defineMessages({
  fieldLabel: {
    id: 'mse_inline_data.alo_auth.otp_input_form.fieldLabel',
    description: 'The label for the OTP input field',
    defaultMessage: 'Enter PIN',
  },
  submitText: {
    id: 'mse_inline_data.alo_auth.otp_input_form.submitText',
    description: 'Text on the submit button',
    defaultMessage: 'Submit',
  },
  successMessage: {
    id: 'mse_inline_data.alo_auth.otp_input_form.successMessage',
    description: 'Success message for email authentication',
    defaultMessage: 'Email has been successfully authenticated.',
  },
});

export type ValueProps = {
  errorMessage?: string;
  otp?: string;
  isSuccess?: boolean;
};

const isValidOTP = (otp: string): boolean => {
  return otp.length === 6;
};

export const OtpInputForm = (props: InlineDatumReactComponentProps) => {
  const {onDone, isDone, datum} = props;

  const value = datum.value as ValueProps;
  const {errorMessage, otp: currentOtp, isSuccess} = value;

  const intl = useIntl();
  const analytics = useAnalytics();

  const [otp, setOTP] = useState(currentOtp ?? '');
  const [canEdit, setCanEdit] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUserModified, setHasUserModified] = useState(false);
  const [attemptStartOtp] = useState(currentOtp ?? '');

  const isFormValid = useMemo(() => {
    return isValidOTP(otp);
  }, [otp]);

  useEffect(() => {
    setCanEdit(!isDone);
    if (isDone) {
      setIsLoading(false);
    }
  }, [isDone]);

  const handleOTPChange = useCallback(
    (newOtp: string) => {
      setOTP(newOtp);
      if (newOtp !== attemptStartOtp) {
        setHasUserModified(true);
      }
    },
    [attemptStartOtp],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValidOTP(otp)) {
        return;
      }

      setCanEdit(false);
      setIsLoading(true);

      onDone({
        type: 'success',
        message: 'OTP Input Form Data',
        extra: {
          outcome: otp,
        },
      });

      analytics.action('otp_input_form_submit_click', {
        otp_length: otp.length,
      });
    },
    [otp, onDone, analytics],
  );

  const getError = () => {
    if (hasUserModified || !errorMessage) {
      return;
    }
    return errorMessage;
  };

  if (isSuccess) {
    return (
      <view.div css={{width: 'fill'}} data-testid="otp-success-message">
        <MetaRow>
          <view.div
            css={{
              margin: 'small',
              stack: 'x',
              gap: 'xsmall',
              alignItems: 'center',
            }}
          >
            <Icon icon={checkCircle} size="xsmall" />
            <FormattedMessage {...messages.successMessage} />
          </view.div>
        </MetaRow>
      </view.div>
    );
  }

  if (!canEdit) {
    if (isLoading) {
      return (
        <view.div
          style={{margin: 20, display: 'flex', justifyContent: 'center'}}
        >
          <Spinner size="medium" />
        </view.div>
      );
    }
    return null;
  }

  return (
    <view.div css={{marginTop: 'small', width: 'fill'}}>
      <LightCard>
        <view.form css={{gap: 'large', stack: 'y'}} onSubmit={handleSubmit}>
          <CodePuncher
            noAutofill
            value={otp}
            onChange={handleOTPChange}
            data-testid="otp-input-form"
            label={intl.formatMessage(messages.fieldLabel)}
            error={getError()}
            subviews={{
              label: {
                css: {
                  marginBottom: 'medium',
                  font: 'heading.small',
                },
              },
              digit: {
                css: {
                  width: '50px',
                  height: '50px',
                },
              },
            }}
          />

          <Button
            css={{width: 'fill'}}
            data-testid="otp-submit-btn"
            type="primary"
            behavior="submit"
            disabled={!isFormValid}
          >
            <FormattedMessage {...messages.submitText} />
          </Button>
        </view.form>
      </LightCard>
    </view.div>
  );
};
